import { ChangeEvent, FC, useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { isValueAnEmail } from '../../../Login/LoginForm';
import { requestResetCodeRequest } from '@vs-online-only-app/state';

type Props = {
  email: string;
  setEmail: (email: string) => void;
  nextStep: () => void;
};

const ResetPasswordStep1: FC<Props> = ({ email, setEmail, nextStep }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');

  const requestResetCode = useCallback(async () => {
    if (!isValueAnEmail(email)) {
      setFieldError('Invalid');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await requestResetCodeRequest({ email });
      nextStep();
    } catch (e) {
      setError('Could not send reset code. Please try again.');
      console.error('Error requesting password reset code', e);
      Sentry.withScope((scope) => {
        scope.setTags({
          source: 'ResetPassword.requestResetCode',
        });
        Sentry.captureException(e, {
          level: 'warning',
        });
      });
    }

    setIsLoading(false);
  }, [email, nextStep]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setFieldError('');
  };

  return (
    <Stack gap={3}>
      <Typography variant="h4" align="center">
        Reset password?
      </Typography>
      <Typography align="center">
        We'll send a reset code to your registered address
      </Typography>
      <TextField
        label={`Email address${fieldError ? ` - ${fieldError}` : ''}`}
        value={email}
        onChange={onChange}
        error={Boolean(fieldError)}
      />
      {error && <Alert severity="error">{error}</Alert>}
      <Button
        disabled={isLoading}
        variant="contained"
        onClick={requestResetCode}
        fullWidth
        size="large"
      >
        {isLoading ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          'Send Code'
        )}
      </Button>
    </Stack>
  );
};

export default ResetPasswordStep1;
